import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Pagination,
  Select,
  MenuItem,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PublicIcon from '@mui/icons-material/Public';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloudIcon from '@mui/icons-material/Cloud';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import NotificationSnackbar, { NotificationType } from 'components/common/NotificationSnackBar';
import HLSPlayer from 'components/common/hls/HLSPlayer';
import { IssueCastListItem, updateIssueCast, deleteIssueCast, IssueCastUpdates } from 'api/apiClient';
import ChaptersPanel, { type Chapter } from 'features/websites/components/common/ChaptersPanel';
import CollapsibleSection from 'components/common/CollapsibleSection';
import IssueCastEditSection from 'features/issuecasts/components/IssueCastEditSection';
import AnalyticsSection from 'features/issuecasts/components/AnalyticsSection';
import { useNavigate } from 'react-router-dom';

const formatDateTime = (iso?: string) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso as string;
  }
};

// Helper function to extract programId (GUID) from hlsUrl
const extractProgramId = (hlsUrl?: string): string | null => {
  if (!hlsUrl) return null;
  // Match GUID pattern: 8-4-4-4-12 hex digits
  const guidPattern = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
  const match = hlsUrl.match(guidPattern);
  return match ? match[1] : null;
};

export interface IssueCastListProps {
  items: IssueCastListItem[];
  setItems: React.Dispatch<React.SetStateAction<IssueCastListItem[]>>;
  loading?: boolean;
  onIssueCastPublished?: () => void; // New: callback to refresh list after publish
}

const IssueCastList: React.FC<IssueCastListProps> = ({ items, setItems, loading = false, onIssueCastPublished }) => {
  const [notification, setNotification] = React.useState({ open: false, message: '', type: 'info' as NotificationType });
  const [expandedId, setExpandedId] = React.useState<string | false>(false);
  const [editValues, setEditValues] = React.useState<IssueCastUpdates | undefined>(undefined);

  // Pagination
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Chapters cache and edited lines state keyed by IssueCast ID
  const [chaptersByIssueId, setChaptersByIssueId] = React.useState<Record<string, Chapter[] | null | undefined>>({});
  const [chaptersLoadingId, setChaptersLoadingId] = React.useState<string | null>(null);
  const [chaptersErrorByIssueId, setChaptersErrorByIssueId] = React.useState<Record<string, string | null>>({});
  const [editedLinesByIssueId, setEditedLinesByIssueId] = React.useState<Record<string, Record<number, Array<{ co_host?: string; line?: string }>>>>({});

  const navigate = useNavigate();

  // Helper: build chapters.json URL from a playlist.m3u8 URL
  const toChaptersUrl = (hlsUrl?: string): string | null => {
    if (!hlsUrl) return null;
    try {
      const u = new URL(hlsUrl);
      u.pathname = u.pathname.replace(/[^/]+$/, 'chapters.json');
      return u.toString();
    } catch {
      if (hlsUrl.endsWith('playlist.m3u8')) return hlsUrl.replace(/playlist\.m3u8$/, 'chapters.json');
      return null;
    }
  };

  React.useEffect(() => {
    const id = expandedId && typeof expandedId === 'string' ? expandedId : null;
    if (!id) return;
    if (chaptersByIssueId[id] !== undefined) return;
    const current = items.find(i => i.id === id);
    const chaptersUrl = toChaptersUrl(current?.hlsUrl);
    if (!chaptersUrl) {
      setChaptersByIssueId(prev => ({ ...prev, [id]: null }));
      setChaptersErrorByIssueId(prev => ({ ...prev, [id]: 'No chapters URL available.' }));
      return;
    }

    let cancelled = false;
    setChaptersLoadingId(id);
    setChaptersErrorByIssueId(prev => ({ ...prev, [id]: null }));

    fetch(chaptersUrl, { headers: { Accept: 'application/json' }, method: 'GET', cache: 'no-store' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const chapters: Chapter[] | null = Array.isArray(data)
          ? data as Chapter[]
          : (Array.isArray((data || {}).chapters) ? (data.chapters as Chapter[]) : null);
        if (!cancelled) {
          setChaptersByIssueId(prev => ({ ...prev, [id]: chapters }));
          if (!chapters) {
            setChaptersErrorByIssueId(prev => ({ ...prev, [id]: 'Stream has no chapters.' }));
          }
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setChaptersByIssueId(prev => ({ ...prev, [id]: null }));
          setChaptersErrorByIssueId(prev => ({ ...prev, [id]: e?.message || 'Failed to load chapters.' }));
        }
      })
      .finally(() => {
        if (!cancelled) setChaptersLoadingId(cur => (cur === id ? null : cur));
      });

    return () => { cancelled = true; };
  }, [expandedId, items, chaptersByIssueId]);

  React.useEffect(() => {
    if (expandedId) {
      const current = items.find(i => i.id === expandedId);
      if (current) {
        setEditValues({
          name: current.name,
          description: current.description,
          hlsUrl: current.hlsUrl,
          publishDate: current.publishDate,
          websiteBranding: current.websiteBranding ? { ...current.websiteBranding } : undefined,
          advertisement: current.advertisement ? { ...current.advertisement } : undefined,
          weather: current.weather ? { ...current.weather } : undefined,
          events: current.events ? { ...current.events } : undefined,
          convai: current.convai ? { ...current.convai } : undefined,
          favicons: current.favicons ? [...current.favicons] : undefined,
          podcasts: current.podcasts ? [...current.podcasts] : undefined,
          podcastType: current.podcastType,
          classification: current.classification ? { ...current.classification } : undefined,
        });
      }
    } else {
      setEditValues(undefined);
    }
  }, [expandedId, items]);

  const closeNotification = () => setNotification(prev => ({ ...prev, open: false }));

  const handleDelete = async (id: string) => {
    const ok = window.confirm('Are you sure you want to delete this podcast? This cannot be undone.');
    if (!ok) return;
    try {
      await deleteIssueCast(id);
      setItems(prev => prev.filter(i => i.id !== id));
      if (expandedId === id) setExpandedId(false);
      setNotification({ open: true, message: 'Podcast deleted.', type: 'success' });
    } catch (e: any) {
      setNotification({ open: true, message: e?.response?.data?.error || 'Failed to delete podcast', type: 'error' });
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editValues) return;
    try {
      const updated = await updateIssueCast(id, editValues);
      setItems(prev => prev.map(i => (i.id === id ? { ...i, ...updated } as IssueCastListItem : i)));
      setNotification({ open: true, message: 'Podcast updated.', type: 'success' });
    } catch (e: any) {
      setNotification({ open: true, message: e?.response?.data?.error || 'Failed to update podcast', type: 'error' });
    }
  };

  const handleCancel = (id: string) => {
    const current = items.find(i => i.id === id);
    if (!current) return;
    setEditValues({
      name: current.name,
      description: current.description,
      hlsUrl: current.hlsUrl,
      publishDate: current.publishDate,
      websiteBranding: current.websiteBranding ? { ...current.websiteBranding } : undefined,
      advertisement: current.advertisement ? { ...current.advertisement } : undefined,
      weather: current.weather ? { ...current.weather } : undefined,
      events: current.events ? { ...current.events } : undefined,
      convai: current.convai ? { ...current.convai } : undefined,
      favicons: current.favicons ? [...current.favicons] : undefined,
      podcasts: current.podcasts ? [...current.podcasts] : undefined,
      podcastType: current.podcastType,
      classification: current.classification ? { ...current.classification } : undefined,
    });
  };

  const handleOpenInStudio = async (ic: IssueCastListItem) => {
    try {
      let chapters: Chapter[] | null = null;
      const cached = chaptersByIssueId[ic.id];
      if (cached === undefined) {
        const chaptersUrl = toChaptersUrl(ic.hlsUrl);
        if (chaptersUrl) {
          try {
            const res = await fetch(chaptersUrl, { headers: { Accept: 'application/json' }, method: 'GET', cache: 'no-store' });
            if (res.ok) {
              const data = await res.json();
              chapters = Array.isArray(data) ? data as Chapter[] : (Array.isArray((data || {}).chapters) ? (data.chapters as Chapter[]) : null);
            }
          } catch {}
        }
      } else if (Array.isArray(cached)) {
        chapters = cached as Chapter[];
      } else {
        chapters = null;
      }

      const websitePlainId = ic.websiteId.replace(/^WEBSITE#/, '');
      const storageKey = `location_station_audio_preview_v1_website_${websitePlainId}`;
      const payload = {
        timestamp: Date.now(),
        showPreview: true,
        isHLSStream: true,
        audioUrl: null,
        response: {
          description: 'Podcast loaded into Studio preview',
          hlsUrl: ic.hlsUrl,
          chapters: chapters,
          podcastName: ic.name,
          podcastDescription: ic.description,
          podcastPublishDate: ic.publishDate,
        },
        chapters: chapters,
      } as const;
      try { localStorage.setItem(storageKey, JSON.stringify(payload)); } catch {}
      const params = new URLSearchParams({ openWebsiteId: websitePlainId, openTab: 'studio' });
      navigate(`/websites?${params.toString()}`);
    } catch (e) {
      setNotification({ open: true, message: 'Failed to open in Studio.', type: 'error' });
    }
  };

  // Paging helpers
  const pagedItems = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return items.slice(start, end);
  }, [items, page, rowsPerPage]);

  const pageCount = React.useMemo(() => Math.max(1, Math.ceil(items.length / rowsPerPage)), [items.length, rowsPerPage]);

  return (
    <>
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">No Podcasts found.</Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={1} sx={{ mb: 2 }} direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Showing {(page - 1) * rowsPerPage + 1}-{Math.min(page * rowsPerPage, items.length)} of {items.length}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">Rows per page</Typography>
                <Select size="small" value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                </Select>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {pagedItems.map((ic) => {
                const websiteName = ic.websiteBranding?.name || 'Unknown Website';
                const websiteLogo = ic.websiteBranding?.logoUrl;
                const locationName = ic.websiteBranding?.locationName;
                const sponsor = ic.advertisement?.name;
                const isExpanded = expandedId === ic.id;
                const isLive = !!ic.isLive;

                return (
                  <Accordion key={ic.id} disableGutters expanded={isExpanded} onChange={(_, exp) => setExpandedId(exp ? ic.id : false)}
                    sx={(theme) => {
                      const bg = alpha(theme.palette.error.main, 0.08);
                      const br = `1px solid ${alpha(theme.palette.error.main, 0.25)}`;
                      return isLive
                        ? {
                            bgcolor: bg,
                            border: br,
                            borderRadius: 1,
                            '&:before': { display: 'none' },
                            '& .MuiAccordionSummary-root': { bgcolor: bg },
                            '& .MuiAccordionDetails-root': { bgcolor: bg },
                          }
                        : {};
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: '100%' }} alignItems={{ xs: 'flex-start', md: 'center' }}>
                        {websiteLogo ? (
                          <Box component="img" src={websiteLogo} alt={websiteName} sx={{ maxHeight: 56, width: 'auto', objectFit: 'contain' }} />
                        ) : (
                          <Box sx={{ width: 56, height: 56, bgcolor: 'action.hover' }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ic.name || websiteName}
                            </Typography>
                            <Chip size="small" icon={<CalendarMonthIcon />} label={formatDateTime(ic.publishDate)} />
                          </Stack>
                          {ic.name && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {websiteName}
                            </Typography>
                          )}
                          {ic.description && (
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                              {ic.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                            {locationName && <Chip size="small" icon={<PublicIcon />} label={locationName} />}
                            {sponsor && <Chip size="small" icon={<CampaignIcon />} label={`Sponsor: ${sponsor}`} />}
                            {ic.weather?.enabled && <Chip size="small" icon={<CloudIcon />} label={`Weather: On${ic.weather?.locationName ? ` (${ic.weather.locationName})` : ''}`} />}
                            {ic.convai?.enabled && <Chip size="small" icon={<SmartToyIcon />} label="ConvAI: On" />}
                            {ic.podcastType === 'emergency' && <Chip size="small" icon={<WarningIcon />} label="Emergency" color="error" />}
                          </Stack>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                          {ic.issueCastURL ? (
                            <Button size="small" variant="outlined" color="error" startIcon={<LinkIcon />} href={ic.issueCastURL} target="_blank" onClick={(e) => e.stopPropagation()}>
                              Live Podcast
                            </Button>
                          ) : null}
                          <Button size="small" variant="outlined" startIcon={<LinkIcon />} href={`${process.env.REACT_APP_ISSUECAST_URL}/?issueCastId=${ic.id}`} target="_blank" onClick={(e) => e.stopPropagation()}>
                            Open Podcast
                          </Button>
                          <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); handleOpenInStudio(ic); }}>
                            Open In Studio
                          </Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={(e) => { e.stopPropagation(); handleDelete(ic.id); }}>
                            Delete
                          </Button>
                          <Tooltip title={`Copy ID: ${ic.id}`}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(ic.id); setNotification({ open: true, message: 'Podcast ID copied to clipboard.', type: 'success' }); }}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {/* Preview */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Preview</Typography>
                          {isExpanded && (
                            <HLSPlayer 
                              src={ic.hlsUrl} 
                              contentId={ic.id} 
                              websiteId={ic.websiteId} 
                              showActions={true}
                              audioOnly={true}
                              hideFeedback={true}
                              hideShare={true}
                              hidePublish={true}
                            />
                          )}
                        </Paper>

                        {/* Chapters */}
                        {isExpanded && (
                          <>
                            {chaptersLoadingId === ic.id ? (
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={18} />
                                  <Typography variant="body2" color="text.secondary">Loading chapters…</Typography>
                                </Box>
                              </Paper>
                            ) : Array.isArray(chaptersByIssueId[ic.id]) && (chaptersByIssueId[ic.id] as Chapter[]).length > 0 ? (
                              <CollapsibleSection
                                title="Chapters"
                                defaultExpanded={true}
                                id={`chapters-${ic.id}`}
                                namespace="issuecast-preview"
                                collapsedSummary={`${(chaptersByIssueId[ic.id] as Chapter[]).length} articles`}
                              >
                                <ChaptersPanel
                                  chapters={(chaptersByIssueId[ic.id] as Chapter[])}
                                  onChaptersChange={(chs) => setChaptersByIssueId(prev => ({ ...prev, [ic.id]: chs as Chapter[] }))}
                                  editedLines={editedLinesByIssueId[ic.id] || {}}
                                  onEditedLinesChange={(map) => setEditedLinesByIssueId(prev => ({ ...prev, [ic.id]: map }))}
                                  onRegenerate={() => { /* disabled on this tab */ }}
                                  disabled
                                  loading={false}
                                  onNotify={(message, severity = 'info') => setNotification({ open: true, message, type: severity as NotificationType })}
                                  websiteName={websiteName}
                                  websiteId={ic.websiteId}
                                  readOnly={true}
                                />
                              </CollapsibleSection>
                            ) : (
                              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                                {chaptersErrorByIssueId[ic.id] || 'This stream has no chapters.'}
                              </Alert>
                            )}
                          </>
                        )}

                        {/* Edit Podcast */}
                        <CollapsibleSection
                          title="Edit Podcast"
                          defaultExpanded={false}
                          id={`edit-${ic.id}`}
                          namespace="issuecast-edit"
                        >
                          <IssueCastEditSection
                            value={editValues}
                            onChange={(v) => setEditValues(v)}
                            websiteId={ic.websiteId}
                            onCancel={() => handleCancel(ic.id)}
                            onUpdate={() => handleUpdate(ic.id)}
                          />
                        </CollapsibleSection>

                        {/* Analytics */}
                        {(() => {
                          const programId = extractProgramId(ic.hlsUrl);
                          if (!programId) return null;
                          
                          // Store the load function for this analytics section
                          let loadAnalyticsFn: (() => void) | null = null;
                          
                          return (
                            <CollapsibleSection
                              title="Analytics"
                              defaultExpanded={false}
                              id={`analytics-${ic.id}`}
                              namespace="issuecast-analytics"
                              collapsedSummary="View streaming analytics"
                              onExpand={() => {
                                // Trigger analytics load when section is expanded
                                if (loadAnalyticsFn) {
                                  loadAnalyticsFn();
                                }
                              }}
                            >
                              <AnalyticsSection
                                programId={programId}
                                onError={(message) => setNotification({ open: true, message, type: 'error' })}
                                onLoad={(fn) => {
                                  // Store the load function when the component provides it
                                  loadAnalyticsFn = fn;
                                }}
                              />
                            </CollapsibleSection>
                          );
                        })()}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Pagination color="primary" count={pageCount} page={page} onChange={(_, v) => setPage(v)} />
              <Typography variant="caption" color="text.secondary">Sorted by updatedAt (most recent first)</Typography>
            </Stack>
          </>
        )}
      </Paper>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
    </>
  );
};

export default IssueCastList;
